import React from "react";
import { FileText, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FilePreviewContent } from "@/components/ui/file-preview";

interface DocumentViewerProps {
  documentId: string;
  title: string;
  fileUrl?: string;
}

const getFileExtension = (url: string): string => {
  const urlParts = url.split("?")[0];
  const extension = urlParts.split(".").pop()?.toLowerCase() || "";
  return extension;
};

export const DocumentViewer = ({ title, fileUrl }: DocumentViewerProps) => {
  const handleDownload = async () => {
    if (!fileUrl) return;

    const extension = getFileExtension(fileUrl);
    const filename = `${title || "document"}.${extension}`;

    try {
      const response = await fetch(fileUrl, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Fetch failed");
      }

      const blob = await response.blob();

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Fetch download failed, trying direct link:", error);

      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!fileUrl) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/60 bg-card">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <h2 className="z-body-md text-foreground truncate max-w-[300px]">
                  {title}
                </h2>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs max-w-[400px] break-words">{title}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex-1 p-2 overflow-hidden">
          <div className="w-full h-full flex flex-col items-center justify-center bg-card rounded-xl border border-border/60 shadow-sm">
            <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium">
              Document preview not available
            </p>
            <p className="text-muted-foreground/70 text-sm mt-1">
              No file URL provided
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/60 bg-card">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <h2 className="z-body-md text-foreground truncate max-w-[300px]">
                {title}
              </h2>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-xs max-w-[400px] break-words">{title}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => window.open(fileUrl, "_blank")}
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1" />
            Open in new tab
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-primary hover:text-primary/80"
            onClick={handleDownload}
          >
            <Download className="w-3.5 h-3.5 mr-1" />
            Download
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <FilePreviewContent fileUrl={fileUrl} fileName={title} />
      </div>
    </div>
  );
};
